import React, { useState } from 'react';
import { FileText, User, Mail, Phone, Building2, Upload, Send, CheckCircle } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Basvurmak = () => {
  const [formData, setFormData] = useState({
    position: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    tcNo: '',
    birthDate: '',
    address: '',
    city: '',
    education: '',
    university: '',
    department: '',
    graduationYear: '',
    workExperience: '',
    currentCompany: '',
    skills: '',
    coverLetter: ''
  });

  const [files, setFiles] = useState({
    cv: null,
    coverLetterFile: null,
    otherDocuments: null
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFiles({ ...files, [e.target.name]: e.target.files[0] });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast({
      title: 'Başvurunuz Alındı',
      description: 'En kısa sürede sizinle iletişime geçeceğiz.',
    });
    // Reset form
    setFormData({
      position: '',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      tcNo: '',
      birthDate: '',
      address: '',
      city: '',
      education: '',
      university: '',
      department: '',
      graduationYear: '',
      workExperience: '',
      currentCompany: '',
      skills: '',
      coverLetter: ''
    });
    setFiles({ cv: null, coverLetterFile: null, otherDocuments: null });
  };

  const positions = [
    'Kıdemli Portföy Yöneticisi',
    'Finansal Analist',
    'Yazılım Geliştirici',
    'İnsan Kaynakları Uzmanı',
    'Müşteri İlişkileri Yöneticisi',
    'Risk Yönetimi Uzmanı',
    'Diğer'
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <div className="relative h-80 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 flex items-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.02) 35px, rgba(255,255,255,.02) 70px)'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center space-x-4 mb-4">
            <div className="bg-orange-500 p-3 rounded-lg">
              <FileText className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-6xl font-bold text-white">Başvuru</h1>
          </div>
          <p className="text-slate-300 text-xl max-w-2xl">
            Ekibimize katılmak için iş başvurunuzu yapın
          </p>
        </div>
      </div>

      {/* Application Form */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Kariyer Gelişimi</h3>
              <p className="text-slate-600 text-sm">Sürekli eğitim ve gelişim fırsatları</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <User className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Profesyonel Ekip</h3>
              <p className="text-slate-600 text-sm">Deneyimli profesyonellerle çalışın</p>
            </div>
            <div className="bg-white rounded-xl p-6 shadow-lg text-center">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Building2 className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">Güvenilir</h3>
              <p className="text-slate-600 text-sm">120 yıllık tecrübe ve güven</p>
            </div>
          </div>

          {/* Main Form */}
          <div className="bg-white rounded-xl shadow-xl p-8">
            <h2 className="text-3xl font-bold text-slate-900 mb-8">İş Başvuru Formu</h2>
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Position */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">Başvurduğunuz Pozisyon *</label>
                <select
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="">Pozisyon Seçiniz</option>
                  {positions.map(pos => (
                    <option key={pos} value={pos}>{pos}</option>
                  ))}
                </select>
              </div>

              {/* Personal Information */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Kişisel Bilgiler</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Ad *</label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Adınız"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Soyad *</label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Soyadınız"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">TC Kimlik No *</label>
                    <input
                      type="text"
                      name="tcNo"
                      value={formData.tcNo}
                      onChange={handleChange}
                      required
                      maxLength="11"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="12345678901"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Doğum Tarihi *</label>
                    <input
                      type="date"
                      name="birthDate"
                      value={formData.birthDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>
                </div>
              </div>

              {/* Contact Information */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">İletişim Bilgileri</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">E-posta *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="ornek@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Telefon *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="0555 123 45 67"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Adres *</label>
                    <textarea
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      rows="3"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                      placeholder="Tam adresiniz"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Şehir *</label>
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="İstanbul"
                    />
                  </div>
                </div>
              </div>

              {/* Education & Experience */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Eğitim ve Deneyim</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Eğitim Seviyesi *</label>
                    <select
                      name="education"
                      value={formData.education}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Seçiniz</option>
                      <option value="lise">Lise</option>
                      <option value="onlisans">Ön Lisans</option>
                      <option value="lisans">Lisans</option>
                      <option value="yukseklisans">Yüksek Lisans</option>
                      <option value="doktora">Doktora</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Üniversite</label>
                    <input
                      type="text"
                      name="university"
                      value={formData.university}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Üniversite adı"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Bölüm</label>
                    <input
                      type="text"
                      name="department"
                      value={formData.department}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Bölüm adı"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Mezuniyet Yılı</label>
                    <input
                      type="text"
                      name="graduationYear"
                      value={formData.graduationYear}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="2020"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">İş Deneyimi (Yıl)</label>
                    <select
                      name="workExperience"
                      value={formData.workExperience}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Seçiniz</option>
                      <option value="0-1">0-1 Yıl</option>
                      <option value="1-3">1-3 Yıl</option>
                      <option value="3-5">3-5 Yıl</option>
                      <option value="5-10">5-10 Yıl</option>
                      <option value="10+">10+ Yıl</option>
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Mevcut/Son Çalıştığınız Şirket</label>
                    <input
                      type="text"
                      name="currentCompany"
                      value={formData.currentCompany}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Şirket adı"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Yetenekler ve Beceriler</label>
                    <textarea
                      name="skills"
                      value={formData.skills}
                      onChange={handleChange}
                      rows="3"
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                      placeholder="Örn: Excel, Python, Finansal Analiz, İngilizce (İleri Seviye)"
                    ></textarea>
                  </div>
                </div>
              </div>

              {/* Document Upload */}
              <div className="border-t border-slate-200 pt-6">
                <h3 className="text-xl font-semibold text-slate-900 mb-4">Belgeler</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      CV / Özgeçmiş * (PDF, DOC, DOCX)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">
                            {files.cv ? files.cv.name : 'CV Dosyanızı Seçin'}
                          </p>
                        </div>
                        <input
                          type="file"
                          name="cv"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Ön Yazı / Motivasyon Mektubu (Opsiyonel)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">
                            {files.coverLetterFile ? files.coverLetterFile.name : 'Dosya Seçin'}
                          </p>
                        </div>
                        <input
                          type="file"
                          name="coverLetterFile"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx"
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Diğer Belgeler (Sertifika, Referans vb.)
                    </label>
                    <div className="flex items-center space-x-4">
                      <label className="flex-1 cursor-pointer">
                        <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-orange-500 transition-colors">
                          <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-sm text-slate-600">
                            {files.otherDocuments ? files.otherDocuments.name : 'Dosya Seçin'}
                          </p>
                        </div>
                        <input
                          type="file"
                          name="otherDocuments"
                          onChange={handleFileChange}
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          className="hidden"
                        />
                      </label>
                    </div>
                  </div>
                </div>
              </div>

              {/* Cover Letter */}
              <div className="border-t border-slate-200 pt-6">
                <label className="block text-sm font-semibold text-slate-700 mb-2">Ön Yazı / Neden Bu Pozisyon?</label>
                <textarea
                  name="coverLetter"
                  value={formData.coverLetter}
                  onChange={handleChange}
                  rows="6"
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 resize-none"
                  placeholder="Neden bu pozisyon için uygun olduğunuzu ve şirketimize katılmak istediğinizi kısaca açıklayın..."
                ></textarea>
              </div>

              {/* Terms */}
              <div className="bg-orange-50 border-l-4 border-orange-500 p-4">
                <p className="text-sm text-slate-700">
                  <strong>Not:</strong> Başvurunuzu göndererek{' '}
                  <a href="/gizlilik-politikasi" className="text-orange-600 hover:text-orange-700 underline">
                    Gizlilik Politikası
                  </a>{' '}
                  ve{' '}
                  <a href="/kullanim-kosullari" className="text-orange-600 hover:text-orange-700 underline">
                    Kullanım Koşulları
                  </a>'nı kabul etmiş olursunuz.
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-white py-4 rounded-lg font-semibold hover:shadow-lg hover:shadow-orange-500/50 transition-shadow flex items-center justify-center space-x-2"
              >
                <span>Başvuruyu Gönder</span>
                <Send className="w-5 h-5" />
              </button>
            </form>
          </div>

          {/* Contact Info */}
          <div className="mt-12 text-center">
            <p className="text-slate-600 mb-4">Sorularınız için bizimle iletişime geçebilirsiniz:</p>
            <a
              href="mailto:musterimemnuniyeti@tmdonlinesube.com"
              className="text-orange-600 hover:text-orange-700 font-semibold"
            >
              musterimemnuniyeti@tmdonlinesube.com
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Basvurmak;
